/**
 * Wrapper hook for the mixed-import (item cleanup v2) endpoint.
 *
 * Delegates to the Orval-generated client for `/company-reports/imports/
 * process-v2/<goodsType>/` (`companyReportsImportsProcessV2Create`), keeping a
 * `FormData`-friendly signature: the endpoint takes a multipart upload, so the
 * call site assembles `FormData` directly rather than a typed `ProcessMixedRows`
 * body. This hook curries `goodsType` and defaults `is_mixed_import=true` so
 * callers can stay agnostic of those details.
 */
import { useAxiosConfig } from '@shared/api/AxiosConfigProvider';
import { companyReportsImportsProcessV2Create } from '@shared/api/lib/company-reports/company-reports';
import { ProcessMixedRows } from '@shared/api/types';
import { useMutation } from '@tanstack/react-query';

export type ImportGoodsType = 'purchased-goods' | 'sold-goods';

export const useProcessMixedImport = (
  goodsType: ImportGoodsType = 'purchased-goods'
) => {
  const requestOptions = useAxiosConfig();
  return useMutation({
    mutationFn: (formData: FormData) => {
      // Flag this as a mixed (classify-first) import. The same
      // /process-v2/<goodsType>/ endpoint accepts is_mixed_import=false for
      // single-type v2 imports.
      if (!formData.has('is_mixed_import')) {
        formData.append('is_mixed_import', 'true');
      }
      // The endpoint is multipart/form-data, so we send the FormData as the
      // request body even though the generated signature types it as the
      // structured ProcessMixedRows shape.
      return companyReportsImportsProcessV2Create(
        goodsType,
        formData as unknown as ProcessMixedRows,
        requestOptions
      );
    },
  });
};
